import { loadScript, on, select } from 'helpers/dom'
import debounce from 'lodash.debounce'
import './video-player.css'

const selectors = {
  VIDEO_PLAYER_SELECTOR: 'video-player',
  VIDEO_THUMBNAIL_SELECTOR: '.js-video-player-thumbnail',
  VIDEO_THUMBNAIL_BUTTON_SELECTOR: '.js-video-player-thumbnail-button',
  HIDDEN_CLASS: 'opacity-0',
  PLAYING_CLASS: 'is-playing',
  LOADING_CLASS: 'is-loading',
  PAUSING_CLASS: 'is-pausing',
  THUMBNAIL_INITIALIZED_CLASS: 'is-thumbnail-initialized',
  VIDEO_TYPES: {
    YOUTUBE: 'youtube',
    VIMEO: 'vimeo',
    MP4: 'mp4'
  },
  STATES: {
    PLAYING: 'playing',
    PAUSING: 'pausing',
    LOADING: 'loading'
  },
  DEBOUNCE_TIME: 200
}

export default class VideoPlayer extends HTMLElement {
  constructor() {
    super()
    this.player = null
    this.element = document.createElement('div')
    this.type =
      this.getAttribute('data-video-type') || selectors.VIDEO_TYPES.MP4
    this.thumbnailEl = null
    this.isLoaded = false
    this.src = this.getAttribute('src') || null
    this.playOnClick = false
    this.status = {
      play: null,
      error: null
    }
    this.options = {
      autoplay: this.hasAttribute('autoplay'),
      controls: this.hasAttribute('controls'),
      playsinline: this.hasAttribute('playsinline'),
      muted: this.hasAttribute('muted'),
      loop: this.hasAttribute('loop'),
      origin: window.location.origin,
      thumbnail: this.getAttribute('data-thumbnail'),
      thumbnailLazyload:
        this.getAttribute('data-thumbnail-lazyload') === 'true',
      playIntersection: this.hasAttribute('data-play-intersection'),
      lazyload: this.hasAttribute('lazyload'),
      useEmbedYoutube:
        !this.hasAttribute('loop') && !this.hasAttribute('autoplay')
    }

    if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
      this.appendChild(this.element)
    }

    if (!this.src) {
      this.status.error = 'No video src'
    }

    if (this.options.playIntersection) {
      this.initWhenVisible()
    }
    this.unload = this.unload.bind(this)
    this.play = this.play.bind(this)
    this.pause = this.pause.bind(this)
    this.initWhenVisible = this.initWhenVisible.bind(this)

    if (this.hasAttribute('auto-init')) {
      this.init()
    }

    if (this.options.lazyload) {
      this.lazyload()
    }

    if (!this.options.controls) {
      this.videoToggle()
    }
  }

  get getVideoID() {
    if (this.getAttribute('data-external-id')) {
      return this.getAttribute('data-external-id')
    }

    if (/youtu\.?be/.test(this.src)) {
      const patterns = [
        /youtu\.be\/([^#&?]{11})/, // youtu.be/<id>
        /\?v=([^#&?]{11})/, // ?v=<id>
        /&v=([^#&?]{11})/, // &v=<id>
        /embed\/([^#&?]{11})/, // embed/<id>
        /\/v\/([^#&?]{11})/ // /v/<id>
      ]

      for (let i = 0; i < patterns.length; ++i) {
        if (patterns[i].test(this.src)) {
          return patterns[i].exec(this.src)[1]
        }
      }
    }

    if (
      /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/.test(
        this.src
      )
    ) {
      const parseURL =
        /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/.exec(
          this.src
        )

      return parseURL[5]
    }

    return null
  }

  initWhenVisible() {
    this.observeVisibility(this).then((isVisible) => {
      if (!this.isLoaded && isVisible) {
        if (this.options.autoplay) {
          !this.thumbnailEl && this.initThumbnail()
        }
        this.src && this.type && this.init()
        this.isLoaded = true
      }
    })
  }

  unload() {
    switch (this.type) {
      case 'youtube':
        this.player.clearVideo()
        break
      case 'vimeo':
        this.player.unload()
        break
    }
  }

  initThumbnail() {
    const thumbnailImage = this.options.thumbnail
      ? `<img src="${this.options.thumbnail}" loading="${this.options.thumbnailLazyload ? 'lazy' : 'eager'}" class="w-full h-full object-cover" alt="thumbnail video" ${this.options.thumbnailLazyload ? '' : 'fetchpriority="high"'} />`
      : ''

    const html = `
      <div class="js-video-player-thumbnail absolute left-0 top-0 w-full h-full bg-grey-500 flex items-center justify-center transition-opacity z-6
        before:absolute before:top-0 before:left-0 before:w-full before:h-full before:bg-overlay">
          ${thumbnailImage}
          <button type="button" class="js-video-player-thumbnail-button rounded-full w-14 h-14 text-inverse absolute flex z-50 items-center justify-center" aria-label="Pause Video">
            <div class="[.is-loading_&]:hidden md-max:[&_svg]:w-10 md-max:[&_svg]:h-10">
              <span class="sr-only">Pause video</span>
              ${GM_STATE.video.iconPause}
            </div>
            <div class="hidden [.is-loading_&]:block">
              <span class="button-loading button-loading-large"></span>
            </div>
          </button>
        </div>
      </div>
      `

    this.thumbnailEl = html
    this.insertAdjacentHTML('beforeend', this.thumbnailEl)
    this.classList.add(selectors.THUMBNAIL_INITIALIZED_CLASS)

    const buttonEl = this.querySelector(
      selectors.VIDEO_THUMBNAIL_BUTTON_SELECTOR
    )

    if (this.options.autoplay) {
      this.setStatePlayer(selectors.STATES.LOADING)
    }
    buttonEl.addEventListener('click', () => {
      if (!this.player) {
        this.setStatePlayer(selectors.STATES.LOADING)
        this.playOnClick = true

        if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
          this.setupYTPlayer()
        }

        if (this.type === selectors.VIDEO_TYPES.VIMEO) {
          this.setupVimeoPlayer()
        }

        if (this.type === selectors.VIDEO_TYPES.MP4) {
          this.setupMP4Player()
        }
      } else {
        if (!this.status.play) {
          this.play()
        } else {
          this.pause()
        }
      }
    })
  }

  initLiteEmbedYoutube() {
    if (!this.querySelector('lite-youtube')) {
      const html = `<lite-youtube videoid="${this.getVideoID}" params="controls=${this.options.controls ? 1 : 0}&playinside=${this.options.playsinline}&loop=${this.options.loop ? 1 : 0}&modestbranding=2&rel=0&enablejsapi=1" ${this.options.thumbnail ? `style="background-image: url('${this.options.thumbnail}')"` : ''}>
        <button type="button" class="lty-playbtn" aria-label="Pause Video">
          ${GM_STATE.video.iconPause}
        </button>
      </lite-youtube>`

      this.insertAdjacentHTML('beforeend', html)
    }
  }

  initYoutubePlayer() {
    if (this.options.thumbnail && !this.thumbnailEl) {
      this.initThumbnail()
    }

    if (this.options.useEmbedYoutube) {
      this.initLiteEmbedYoutube()
    } else {
      YT.ready(() => this.setupYTPlayer())
    }
  }

  initVimeoPlayer() {
    if (!this.options.autoplay && this.options.thumbnail) {
      !this.thumbnailEl && this.initThumbnail()
    } else {
      this.setupVimeoPlayer()
    }
  }

  initMP4Player() {
    if (this.querySelector('video')) return

    if (!this.options.autoplay && this.options.thumbnail) {
      !this.thumbnailEl && this.initThumbnail()
    } else {
      this.setupMP4Player()
    }
  }

  async initByType(type) {
    switch (type) {
      case selectors.VIDEO_TYPES.YOUTUBE:
        this.initYoutubePlayer()
        break
      case selectors.VIDEO_TYPES.VIMEO:
        this.initVimeoPlayer()
        break
      case selectors.VIDEO_TYPES.MP4:
        this.initMP4Player()
        break
    }
  }

  async loadPlayerAPIs(type) {
    let src = ''

    if (
      type === selectors.VIDEO_TYPES.YOUTUBE &&
      !window.YT &&
      !this.options.useEmbedYoutube
    ) {
      src = 'https://www.youtube.com/iframe_api'
    } else if (type === selectors.VIDEO_TYPES.VIMEO && !window.Vimeo) {
      src = 'https://player.vimeo.com/api/player.js'
    } else {
      return
    }

    return loadScript(src)
  }

  setupYTPlayer() {
    const options = {
      videoId: this.getVideoID,
      playerVars: {
        autoplay: 0,
        controls: this.options.controls ? 1 : 0,
        height: '100%',
        width: '100%',
        playsinline: this.options.playsinline ? 1 : 0,
        loop: this.options.loop ? 1 : 0
      },
      events: {
        onError: () => {
          this.status.error = new Error('player could not be built')
        },
        onStateChange: (e) => this._onYouTubeStateChange(e),
        onReady: (e) => this._onYoutubeReadyState(e)
      }
    }

    if (YT.loaded) {
      this.player = new window.YT.Player(this.element, options)
    }
  }

  setupVimeoPlayer() {
    if (this.player) return

    const options = {
      id: this.getVideoID,
      ...this.options
    }

    this.player = new window.Vimeo.Player(this, options)

    if (!this.options.autoplay) {
      this.player.ready().then(() => {
        this.play()
        this.setStatePlayer(selectors.STATES.PLAYING)
        this.displayThumbnail(false)
      })
    } else {
      this.player.ready().then(() => {
        this.play()
        this.displayThumbnail(false)
      })
    }

    this.player.on('play', async (data) => {
      const isPaused = await this.player.getPaused()
      const isEnded = await this.player.getEnded()
      const currentTime = data?.seconds || 0

      if (
        (!isPaused && !isEnded && currentTime > 0 && this.options.controls) ||
        !this.options.controls
      ) {
        this.onPlay()
      }
    })
    this.player.on('pause', () => this.onPause())
    this.player.on('ended', () => this.onEnd)
  }

  setupMP4Player() {
    this.videoEl = document.createElement('video')
    this.videoEl.src = this.src
    this.player = this.videoEl
    this.appendChild(this.videoEl)

    if (!this.options.autoplay) {
      this.play()
      this.setStatePlayer(selectors.STATES.PLAYING)
    }

    this.displayThumbnail(false)
    this.player.controls = this.options.controls
    this.player.autoplay = this.options.autoplay
    this.player.playsInline = this.options.playsinline
    this.player.muted = this.options.muted
    this.player.loop = this.options.loop

    this.player.addEventListener('play', () => {
      if (
        (!this.videoEl.paused &&
          this.videoEl.currentTime > 0 &&
          !this.videoEl.ended &&
          this.options.controls) ||
        !this.options.controls
      ) {
        this.onPlay()
      }
    })
    this.player.addEventListener('pause', () => this.onPause())
    this.player.addEventListener('ended', () => this.onEnd())
  }

  play() {
    try {
      if (!this.player) return

      if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
        this.player.playVideo()
      } else {
        this.player.play()
      }
    } catch (error) {
      this.status.error = error
    }
  }

  pause() {
    try {
      if (!this.player) return

      if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
        this.player.pauseVideo()
      } else {
        this.player.pause()
      }
    } catch (error) {
      this.status.error = error
    }
  }

  onPlay() {
    this.play()
    this.status.play = true
    this.dispatchEvent(new CustomEvent('play'))
    this.setStatePlayer(selectors.STATES.PLAYING)
    this.displayThumbnail(false)
  }

  onPause() {
    this.pause()
    this.status.play = false
    this.dispatchEvent(new CustomEvent('pause'))
    this.setStatePlayer(selectors.STATES.PAUSING)
    this.displayThumbnail(true)
  }

  onEnd() {
    if (this.options.loop) {
      this.play()
    } else {
      this.status.play = false
      this.dispatchEvent(new CustomEvent('ended'))
      this.displayThumbnail(true)
      this.setStatePlayer(null)
    }
  }

  setStatePlayer(status) {
    switch (status) {
      case selectors.STATES.PLAYING:
        this.classList.add(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.PAUSING_CLASS)
        this.classList.remove(selectors.LOADING_CLASS)
        break
      case selectors.STATES.PAUSING:
        this.classList.add(selectors.PAUSING_CLASS)
        this.classList.remove(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.LOADING_CLASS)
        break
      case selectors.STATES.LOADING:
        this.classList.add(selectors.LOADING_CLASS)
        this.classList.remove(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.PAUSING_CLASS)
        break
      default:
        this.classList.remove(selectors.LOADING_CLASS)
        this.classList.remove(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.PAUSING_CLASS)
    }
  }

  displayThumbnail(status) {
    const el = this.querySelector(selectors.VIDEO_THUMBNAIL_SELECTOR)

    if (!el) return

    if (status) {
      el.classList.remove(selectors.HIDDEN_CLASS)
      el.classList.add('visible')
      el.classList.remove('invisible')
    } else {
      el.classList.add(selectors.HIDDEN_CLASS)
      el.classList.remove('visible')
      el.classList.add('invisible')
    }
  }

  _onYouTubeStateChange(e) {
    const stateMap = {
      '-1': 'unstarted',
      0: 'ended',
      1: 'playing',
      2: 'pause',
      3: 'buffering',
      5: 'cued'
    }
    const state = stateMap[e.data.toString()]
    const eventMethodMap = {
      ended: this.onEnd,
      pause: this.onPause,
      playing: this.onPlay
    }

    if (eventMethodMap[state]) {
      const method = eventMethodMap[state]

      if (method) {
        method.call(this)
      }
    }
  }

  _onYoutubeReadyState() {
    if (this.options.autoplay || this.playOnClick) {
      this.setStatePlayer(selectors.STATES.PLAYING)
      this.play()
      this.displayThumbnail(false)
    }

    if (this.options.muted && this.player?.mute) {
      this.player.mute()
    }
  }

  checkPartiallyVisibility(entry) {
    const { top, bottom } = entry.boundingClientRect
    const topIsVisible = top > 0 && top < window.innerHeight
    const bottomIsVisible = bottom > 0 && bottom <= window.innerHeight

    return topIsVisible || bottomIsVisible
  }

  observeVisibility(element) {
    return new Promise((resolve) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const partiallyVisible = this.checkPartiallyVisibility(entry)

          resolve(entry.isIntersecting || partiallyVisible)
        })
      })

      observer.observe(element)
    })
  }

  lazyload() {
    const observer = new IntersectionObserver(
      debounce((entries) => {
        entries.forEach((entry) => {
          if (this.options.lazyload) {
            const partiallyVisible = this.checkPartiallyVisibility(entry)

            if (entry.isIntersecting || partiallyVisible) {
              if (!this.isLoaded) {
                this.initWhenVisible()
              } else if (this.options.autoplay) {
                this.player && this.onPlay()
              }
            } else {
              this.player && this.onPause()
            }
          }
        })
      }, selectors.DEBOUNCE_TIME)
    )

    observer.observe(this)
  }

  videoToggle() {
    const html =
      !this.thumbnailEl && !this.options.thumbnail
        ? `<div class="absolute left-0 top-0 w-full h-full z-5 flex justify-center items-center cursor-pointer bg-[rgba(0,0,0,0.2)] [.is-playing_&]:bg-transparent js-video-toggle">
            <div class="rounded-full text-inverse absolute h-14 w-14 flex z-50 items-center justify-center [.is-loading_&]:hidden [.is-playing_&]:hidden">
              <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56" fill="none">
                <g opacity="0.8">
                  <path d="M49.8969 26.5125L18.4187 7.2625C18.1538 7.09904 17.85 7.00923 17.5387 7.00235C17.2274 6.99547 16.92 7.07175 16.648 7.22333C16.376 7.37491 16.1494 7.5963 15.9916 7.86467C15.8337 8.13303 15.7503 8.43865 15.75 8.74999V47.25C15.7503 47.5613 15.8337 47.867 15.9916 48.1353C16.1494 48.4037 16.376 48.6251 16.648 48.7767C16.92 48.9282 17.2274 49.0045 17.5387 48.9976C17.85 48.9908 18.1538 48.901 18.4187 48.7375L49.8969 29.4875C50.1554 29.3346 50.3695 29.1171 50.5183 28.8562C50.6671 28.5954 50.7454 28.3003 50.7454 28C50.7454 27.6997 50.6671 27.4046 50.5183 27.1438C50.3695 26.8829 50.1554 26.6653 49.8969 26.5125Z" fill="white"/>
                </g>
              </svg>
            </div>
          </div>`
        : '<div class="absolute left-0 top-0 w-full h-full z-5 flex justify-center items-center cursor-pointer js-video-toggle"></div>'

    this.insertAdjacentHTML('beforeend', html)

    const buttonVideoToggle = select('.js-video-toggle', this)

    if (buttonVideoToggle) {
      on(
        'click',
        () => {
          if (!this.player) {
            throw new Error('player is undefined')
          }

          if (this.status.play) {
            this.onPause()
          } else {
            this.onPlay()
          }
        },
        buttonVideoToggle
      )
    }
  }

  async init() {
    this.type !== selectors.VIDEO_TYPES.MP4 &&
      (await this.loadPlayerAPIs(this.type))
    this.initByType(this.type)
  }
}

if (!customElements.get('video-player')) {
  customElements.define('video-player', VideoPlayer)
}
